import { FileText, ShieldCheck, Zap } from 'lucide-react';

type StatCard = {
  icon: 'file' | 'shield' | 'zap';
  title: string;
  pill: { label: string; tone: 'amber' | 'violet' | 'teal' };
  subtitle: string;
  rightLabel: string;
  rightValue: string;
  rightTone?: 'danger' | 'neutral' | 'success';
};

const STATS: readonly StatCard[] = [
  {
    icon: 'file',
    title: 'CLM-2026-48291',
    pill: { label: 'HIGH VALUE', tone: 'amber' },
    subtitle: 'BCBS Michigan · CARC 50 medical necessity',
    rightLabel: 'AT RISK',
    rightValue: '$12,450',
    rightTone: 'danger',
  },
  {
    icon: 'file',
    title: '7 appeal drafts ready for review',
    pill: { label: 'HUMAN-IN-LOOP', tone: 'violet' },
    subtitle: 'Appeal writer · generated 2m ago',
    rightLabel: 'AWAITING',
    rightValue: 'Your approval',
    rightTone: 'neutral',
  },
  {
    icon: 'zap',
    title: '142 denials triaged today',
    pill: { label: 'AUTO-ROUTED', tone: 'teal' },
    subtitle: 'Recovery scorer · priority-queued',
    rightLabel: 'RECOVERY',
    rightValue: '87%',
    rightTone: 'success',
  },
];

function renderIcon(icon: StatCard['icon']) {
  const cls = 'h-4 w-4 text-primary-300';
  if (icon === 'file') return <FileText className={cls} />;
  if (icon === 'shield') return <ShieldCheck className={cls} />;
  return <Zap className={cls} />;
}

function pillClasses(tone: StatCard['pill']['tone']): string {
  switch (tone) {
    case 'amber':
      return 'bg-amber-400/10 text-amber-300 ring-amber-300/20';
    case 'violet':
      return 'bg-violet-400/10 text-violet-300 ring-violet-300/20';
    case 'teal':
    default:
      return 'bg-primary-400/10 text-primary-300 ring-primary-300/25';
  }
}

function rightValueClasses(tone: StatCard['rightTone']): string {
  switch (tone) {
    case 'danger':
      return 'text-rose-300';
    case 'success':
      return 'text-primary-300';
    default:
      return 'text-white';
  }
}

export function ShowcasePanel() {
  return (
    <div className="relative flex h-full w-full flex-col justify-between overflow-hidden bg-surface px-10 py-14 lg:px-14 lg:py-16">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_80%_at_0%_0%,rgba(204,0,0,0.18)_0%,rgba(16,10,10,0)_55%)]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(90%_60%_at_100%_100%,rgba(204,0,0,0.10)_0%,rgba(16,10,10,0)_60%)]"
      />

      <div className="relative">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-primary-400">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary-400" />
          Revenue Cycle Intelligence
        </div>
        <h2 className="mt-6 text-4xl font-semibold leading-[1.1] tracking-tight text-white sm:text-[44px]">
          Every denial.{' '}
          <span className="text-primary-400">Analyzed, appealed, and recovered.</span>
        </h2>
        <p className="mt-6 max-w-lg text-[15px] leading-relaxed text-surface-muted">
          NexaClaim AI ingests 835s the moment they land, scores every denial by
          recovery probability, drafts cited appeal letters, and routes revenue back to
          your approval queue — with an auditable trail for every decision.
        </p>
      </div>

      <div className="relative mt-10 space-y-3">
        {STATS.map((stat, idx) => (
          <div
            key={idx}
            className="flex items-center gap-4 rounded-xl border border-surface-border bg-surface-raised/80 px-4 py-3.5 backdrop-blur-sm"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-surface-border bg-surface">
              {renderIcon(stat.icon)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate text-sm font-semibold text-white">
                  {stat.title}
                </span>
                <span
                  className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 ring-inset ${pillClasses(stat.pill.tone)}`}
                >
                  {stat.pill.label}
                </span>
              </div>
              <div className="mt-1 truncate text-xs text-surface-muted">
                {stat.subtitle}
              </div>
            </div>
            <div className="shrink-0 text-right">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-surface-muted">
                {stat.rightLabel}
              </div>
              <div
                className={`mt-0.5 text-sm font-semibold ${rightValueClasses(stat.rightTone)}`}
              >
                {stat.rightValue}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
